import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createMerchant } from 'src/app/api/MerchantApi';
import useAuthUser from './useAuthUser';
import { IMerchantData, ILocalStoreKey, IMerchantCreateRequest } from '../types';
import useLocalStore from './useLocalStore';
import { IBusinessInfoFormValues } from '../main/merchant-signin/forms/BusinessInfoForm';
import { IBusinessNumbersFormValues } from '../main/merchant-signin/forms/BusinessNumbersForm';
import { IBusinessPreferencesFormValues } from '../main/merchant-signin/forms/BusinessPreferencesForm';
import { IBusinessRepresentativeFormValues } from '../main/merchant-signin/forms/BusinessRepresentativeForm';
import { pixelTrackEvent } from '../api/FacebookTrackingApi';
import { fullPhoneNumber } from '../api/utils';

const postMerchant = async (
  businessInfo: IBusinessInfoFormValues,
  businessNumbers: IBusinessNumbersFormValues,
  businessPreferences: IBusinessPreferencesFormValues,
  businessRepresentative: IBusinessRepresentativeFormValues,
) => {
  const features = businessPreferences?.features?.filter((item) => item !== 'Other') || [];
  const data: IMerchantCreateRequest = {
    businessName: businessInfo.businessName,
    dba: businessInfo.dba,
    businessLocation: businessInfo.businessLocation,
    state: businessInfo.businessLocation,
    multipleLocationsInTheSameState: businessInfo.isSameState,
    locationInOtherStates: businessInfo.isMultipleLocation,
    businessStructure: businessInfo.structure,
    isCardRetail: businessInfo.isCardRetail,
    isCardECommerce: businessInfo.isCardECommerce,
    mcc: +(businessInfo.classification.id ?? 0),
    website: businessInfo.website,
    establishedInYear: businessNumbers.establishedInYear,
    minimumMonthlyVolume: businessNumbers.minimumMonthlyVolume,
    highestSingleTransaction: businessNumbers.highestSingleTransaction.split(',').join(''),
    needEquipment: businessPreferences.needEquipment,
    equipment: businessPreferences.equipment ? businessPreferences.equipment : 'none',
    needMobilePayments: businessPreferences.paymentsSupport,
    isAcceptingCards: businessPreferences.acceptCards,
    promptForCreateNovoAccount: businessPreferences.promptForCreateNovoAccount,
    additionalFeatures: businessPreferences.additionalFeatures
      ? [...features, businessPreferences.additionalFeatures]
      : [...businessPreferences.features],

    averageTicket: +businessNumbers.averageTicket.split(',').join(''),
    ...businessRepresentative,
    completeOnboarding: true,
    phoneNumber: fullPhoneNumber(businessRepresentative.phoneNumber)!,
    // Peptide-specific fields
    isPeptideLicensedOrCertified: businessInfo.isPeptideLicensedOrCertified ?? false,
    hasPeptideSalesExperience: businessInfo.hasPeptideSalesExperience ?? false,
    interestedInACH: false,
    interestedInCashlessATM: false,
    interestedInCryptocurrency: false,
  };
  return createMerchant(data);
};

const useUserSignIn = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isAlreadySend, setIsAlreadySend] = useState<boolean>(false);

  const { isLoading: auth0Loading, isAuthenticated } = useAuth0();
  const user = useAuthUser();

  const navigate = useNavigate();
  const { dataFromStore, removeStoreValue } = useLocalStore<IMerchantData>(
    ILocalStoreKey.merchantFormData,
    {
      defaultValue: {
        current: 0,
        data: [],
      },
    },
  );

  const businessData = dataFromStore.data;

  const { businessInfo, businessNumbers, businessPreferences, businessRepresentative } =
    useMemo(() => {
      return {
        businessInfo: businessData[0] as IBusinessInfoFormValues,
        businessNumbers: businessData[1] as IBusinessNumbersFormValues,
        businessPreferences: businessData[2] as IBusinessPreferencesFormValues,
        businessRepresentative: businessData[3] as IBusinessRepresentativeFormValues,
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
  const handleSignIn = useCallback(async () => {
    if (user?.onboarding && !auth0Loading && isAuthenticated && businessData[3]?.email) {
      setIsLoading(true);

      removeStoreValue();
      navigate(`/${user?.role}/dashboard`);
      setIsLoading(false);
    }
    if (
      !user?.onboarding &&
      !auth0Loading &&
      isAuthenticated &&
      businessData?.[3]?.email &&
      !isAlreadySend
    ) {
      setIsAlreadySend(true);
      setIsLoading(true);
      const res = await postMerchant(
        businessInfo,
        businessNumbers,
        businessPreferences,
        businessRepresentative,
      );
      if (res.data?.redirectTo) {
        window.location.replace(res.data.redirectTo);
        return;
      }
      removeStoreValue();

      navigate('/merchant/dashboard');
      window.location.reload();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth0Loading, user?.onboarding, user?.role, businessData]);

  return { handleSignIn, isLoading };
};

export default useUserSignIn;
